/**
 * Submission Triage — the "Saved Views" toolbar control (design OR-1507).
 *
 * A pill in the toolbar row, after Customize, opening a popover that
 * lists the user's saved views. Empty until they save one; each row
 * toggles the view on or off and carries a trash icon; "+ Save current
 * view" sits at the bottom and is the only entry point to the modal.
 *
 * The trigger's label follows the design: "Saved Views" while nothing is
 * applied, the view's own name once one is, and a count once more than
 * one is combined. Built on the same react-aria `DialogTrigger` +
 * `Popover` primitives as `facility-flag-popover`, so focus management
 * and outside-click dismiss come for free.
 */
import { useState } from "react";
import {
    Button as AriaButton,
    Dialog as AriaDialog,
    DialogTrigger as AriaDialogTrigger,
    Popover as AriaPopover,
} from "react-aria-components";
import { Bookmark, ChevronDown, Trash02 } from "@untitledui/icons";
import { FeaturedIcon, cx } from "@tricura/atlas-ui";
import type { SavedView } from "./view-preference-config";

interface SavedViewsMenuProps {
    views: SavedView[];
    /** Ids currently applied — the design allows more than one at once. */
    appliedIds: string[];
    onToggle: (id: string) => void;
    onDelete: (id: string) => void;
    onSaveCurrent: () => void;
    /** Icon-only trigger for the mobile toolbar row. */
    compact?: boolean;
    /** Held while the saved views are still loading: a save attempted in
     *  that window is refused, so the control should not invite one. */
    isDisabled?: boolean;
}

/** What the trigger reads. One applied view names the button; several
 *  fall back to a count, since only one name fits the pill. */
function triggerLabel(views: SavedView[], appliedIds: string[]): string {
    if (appliedIds.length === 0) return "Saved Views";
    if (appliedIds.length === 1) {
        return views.find((v) => v.id === appliedIds[0])?.name ?? "Saved Views";
    }
    return `${appliedIds.length} Saved Views`;
}

const PILL =
    "border-primary inline-flex h-9 cursor-pointer items-center gap-2 rounded-lg border bg-primary px-3 text-sm font-semibold transition duration-100 ease-linear";

export function SavedViewsMenu({
    views,
    appliedIds,
    onToggle,
    onDelete,
    onSaveCurrent,
    compact = false,
    isDisabled = false,
}: SavedViewsMenuProps) {
    const label = triggerLabel(views, appliedIds);
    // Controlled so "+ Save current view" can hand off to the modal: an
    // open react-aria popover keeps a dismissable layer over the page,
    // which would swallow clicks on the toolbar behind it.
    const [isOpen, setIsOpen] = useState(false);

    return (
        <AriaDialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
            <AriaButton
                isDisabled={isDisabled}
                aria-label={compact ? "Saved Views" : undefined}
                data-autotest="triage-toolbar-button-saved-views"
                className={cx(
                    compact
                        ? "border-primary bg-primary text-fg-quaternary hover:bg-secondary hover:text-fg-secondary relative inline-flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-lg border shadow-xs transition duration-100 ease-linear"
                        : cx(PILL, "text-primary shadow-xs hover:bg-secondary max-w-56"),
                    "focus-visible:ring-focus-ring outline-none focus-visible:ring-2",
                    "disabled:cursor-not-allowed disabled:opacity-50",
                )}
            >
                <Bookmark aria-hidden="true" className="text-fg-quaternary size-[17px] shrink-0 stroke-[2.25px]" />
                {!compact && (
                    <>
                        <span className="truncate">{label}</span>
                        <ChevronDown aria-hidden="true" className="text-fg-quaternary size-4 shrink-0 stroke-[2.25px]" />
                    </>
                )}
                {compact && appliedIds.length > 0 && (
                    <span className="bg-fg-brand-primary absolute -right-1 -top-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold leading-none text-white">
                        {appliedIds.length}
                    </span>
                )}
            </AriaButton>

            <AriaPopover
                placement="bottom start"
                offset={6}
                className="entering:animate-in entering:fade-in entering:zoom-in-95 z-50"
            >
                <AriaDialog className="bg-primary border-secondary w-max min-w-[220px] max-w-[280px] overflow-hidden rounded-lg border shadow-lg outline-none">
                    {views.length === 0 ? <EmptyState /> : <ViewList views={views} appliedIds={appliedIds} onToggle={onToggle} onDelete={onDelete} />}

                    <div className={cx(views.length > 0 && "border-secondary mt-1 border-t")}>
                        <button
                            type="button"
                            onClick={() => {
                                setIsOpen(false);
                                onSaveCurrent();
                            }}
                            data-autotest="triage-saved-views-button-save-current"
                            className="text-brand-secondary hover:bg-secondary flex w-full cursor-pointer items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold transition duration-100 ease-linear"
                        >
                            <span aria-hidden="true">+</span> Save current view
                        </button>
                    </div>
                </AriaDialog>
            </AriaPopover>
        </AriaDialogTrigger>
    );
}

function EmptyState() {
    return (
        <div className="flex flex-col items-center px-5 pb-3 pt-5 text-center">
            {/* Bordered square with the glyph in the text tone, per the
              * design's empty state — atlas's "modern" featured icon is
              * exactly that treatment (white ground, light inset ring,
              * rounded corners) with `gray` putting the glyph on
              * `text-fg-secondary`. */}
            <FeaturedIcon icon={Bookmark} theme="modern" color="gray" size="sm" />
            <p className="text-primary mt-3 text-[15px] font-semibold">No saved views yet</p>
            <p className="text-tertiary mt-1 text-[15px]">Save your filters and/or columns as a view you can reuse anytime.</p>
        </div>
    );
}

function ViewList({
    views,
    appliedIds,
    onToggle,
    onDelete,
}: Pick<SavedViewsMenuProps, "views" | "appliedIds" | "onToggle" | "onDelete">) {
    return (
        <>
            <p className="bg-secondary text-quaternary px-3 py-1.5 text-xs font-medium">Saved Views</p>
            <ul className="max-h-72 overflow-y-auto">
                {views.map((view) => {
                    const applied = appliedIds.includes(view.id);
                    return (
                        <li key={view.id} className="hover:bg-secondary group flex items-start gap-2 px-3 py-2 transition duration-100 ease-linear">
                            <button
                                type="button"
                                onClick={() => onToggle(view.id)}
                                aria-pressed={applied}
                                // Long names wrap onto multiple lines rather
                                // than truncating — the design shows a
                                // three-line example.
                                className={cx(
                                    "flex-1 cursor-pointer text-left text-sm",
                                    applied ? "text-primary font-semibold" : "text-secondary",
                                )}
                            >
                                {view.name}
                            </button>
                            <button
                                type="button"
                                onClick={() => onDelete(view.id)}
                                aria-label={`Delete ${view.name}`}
                                className="text-fg-quaternary hover:text-fg-error-primary mt-0.5 shrink-0 cursor-pointer transition duration-100 ease-linear"
                            >
                                <Trash02 aria-hidden="true" className="size-4" />
                            </button>
                        </li>
                    );
                })}
            </ul>
        </>
    );
}
